import React from "react";
import styled from "styled-components";
import { useProvided } from "nonaction";
import { PrintSettingsContainer } from "../../Container";
import FontPicker from "./FontPicker.js";

const Settings = ({ className, onClose }) => {
  const [settings, updateSettings] = useProvided(PrintSettingsContainer);
  return (
    <div className={className} role="presentation" onMouseDown={onClose}>
      <section
        className="dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="settings-title"
        onMouseDown={event => event.stopPropagation()}
      >
        <div className="header">
          <h2 id="settings-title">Settings</h2>
          <button type="button" onClick={onClose} aria-label="Close settings">
            ×
          </button>
        </div>

        <div className="font-setting">
          <span>Font</span>
          <FontPicker className="font-picker" />
        </div>

        <label>
          <span>Paper size</span>
          <select
            value={settings.pageSize}
            onChange={event => updateSettings({ pageSize: event.target.value })}
          >
            <option value="A4">A4</option>
            <option value="Letter">Letter</option>
            <option value="custom">Custom</option>
          </select>
        </label>

        {settings.pageSize === "custom" && (
          <div className="custom-paper">
            <label>
              <span>Width (mm)</span>
              <input
                type="number"
                min="50"
                max="500"
                value={settings.customWidth}
                onChange={event => updateSettings({ customWidth: event.target.value })}
              />
            </label>
            <label>
              <span>Height (mm)</span>
              <input
                type="number"
                min="50"
                max="500"
                value={settings.customHeight}
                onChange={event => updateSettings({ customHeight: event.target.value })}
              />
            </label>
          </div>
        )}

        <label>
          <span>Orientation</span>
          <select
            value={settings.orientation}
            onChange={event => updateSettings({ orientation: event.target.value })}
          >
            <option value="portrait">Portrait</option>
            <option value="landscape">Landscape</option>
          </select>
        </label>

        <label>
          <span>Margin (mm)</span>
          <input
            type="number"
            min="0"
            max="40"
            step="1"
            value={settings.margin}
            onChange={event => updateSettings({ margin: event.target.value })}
          />
        </label>

        <label>
          <span>Scale (%)</span>
          <input
            type="number"
            min="50"
            max="150"
            step="5"
            value={settings.scale}
            onChange={event => updateSettings({ scale: event.target.value })}
          />
        </label>

        <label>
          <span>Header</span>
          <input
            type="text"
            value={settings.header}
            placeholder="Optional header"
            onChange={event => updateSettings({ header: event.target.value })}
          />
        </label>

        <label>
          <span>Footer</span>
          <input
            type="text"
            value={settings.footer}
            placeholder="Optional footer"
            onChange={event => updateSettings({ footer: event.target.value })}
          />
        </label>

        <label className="checkbox">
          <input
            type="checkbox"
            checked={settings.singlePage}
            onChange={event => updateSettings({ singlePage: event.target.checked })}
          />
          <span>Single page</span>
        </label>

        <label className="checkbox">
          <input
            type="checkbox"
            checked={settings.printBackgrounds}
            onChange={event =>
              updateSettings({ printBackgrounds: event.target.checked })
            }
          />
          <span>Print background colors</span>
        </label>

        <label className="checkbox">
          <input
            type="checkbox"
            checked={settings.allowHtml}
            onChange={event => updateSettings({ allowHtml: event.target.checked })}
          />
          <span>Allow inline HTML and styles</span>
        </label>

        <label className="checkbox">
          <input
            type="checkbox"
            checked={settings.pageNumbers}
            onChange={event => updateSettings({ pageNumbers: event.target.checked })}
          />
          <span>Show page numbers</span>
        </label>

        <label className="checkbox">
          <input
            type="checkbox"
            checked={settings.tableOfContents}
            onChange={event => updateSettings({ tableOfContents: event.target.checked })}
          />
          <span>Generate table of contents</span>
        </label>

        <p className="hint">
          These settings are saved in this browser and apply to the next PDF export.
        </p>
      </section>
    </div>
  );
};

export default styled(Settings)`
  position: fixed;
  inset: 0;
  z-index: 100;
  display: flex;
  align-items: flex-start;
  justify-content: flex-end;
  padding: 52px 12px 12px;
  background: rgba(0, 0, 0, 0.22);
  color: #222;

  .dialog {
    width: min(360px, 100%);
    padding: 16px;
    border: 1px solid #999;
    border-radius: 6px;
    background: white;
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.24);
  }

  .header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 14px;

    h2 {
      margin: 0;
      font-size: 20px;
    }

    button {
      border: 0;
      background: transparent;
      font-size: 24px;
      line-height: 1;
      cursor: pointer;
    }
  }

  label {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    margin: 10px 0;

    select,
    input[type="number"],
    input[type="text"] {
      width: 140px;
      min-height: 30px;
    }
  }

  .custom-paper {
    padding-left: 12px;
    border-left: 2px solid #ddd;
  }

  .font-setting {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    margin: 10px 0;
  }

  .font-picker {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 3px;
    flex: 1;

    button,
    select {
      min-height: 30px;
      border: 1px solid #999;
      border-radius: 3px;
      background: white;
    }

    button {
      cursor: pointer;
    }

    select {
      min-width: 130px;
      max-width: 180px;
    }

    .font-message {
      color: #b00020;
      font-weight: bold;
      cursor: help;
    }
  }

  .checkbox {
    justify-content: flex-start;

    input {
      margin: 0;
    }
  }

  .hint {
    margin: 16px 0 0;
    color: #666;
    font-size: 12px;
    line-height: 1.4;
  }
`;
